"use client"
import React, { useState, useEffect, useRef } from "react";
import EmbeddedNavbar from "@/components/maps/embed/EmbeddedNavbar";
import EmbeddedMapViewWrapper from "@/components/maps/embed/EmbeddedMapViewWrapper";
import Modal from "react-bootstrap/Modal";
import ParentContentThumbnail from "@/components/shared/ParentContentThumbnail";
import { useRouter, useSearchParams } from "next/navigation";

import useApi from "@/components/api/useApi";
import { useConfig } from "@/lib/providers/ConfigProvider";

import UralityBrandedModal from "@/components/shared/UralityBrandedModal";
import "@/components/maps/embed/EmbeddedMapWidget.css";

const EmbeddedMapWidget = props => {
  const [menuTrigger, setMenuTrigger] = useState(true);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [map, setMap] = useState(null);
  const [community, setCommunity] = useState(null);
  const [points, setPoints] = useState([]);
  const [title, setTitle] = useState(null);
  const [headerLogoId, setHeaderLogoId] = useState(null);
  const [thumbnailContent, setThumbnailContent] = useState(null);
  const [overrideLayers, setOverrideLayers] = useState(null);
  const [variant, setVariant] = useState("light");
  const api = useApi();
  const searchParams = useSearchParams();
  let unfilteredPoints = useRef([]);

  const router = useRouter();

  const triggerSideMenu = () => {
    setMenuTrigger(!menuTrigger);
  };

  const selectAllLayers = () => {
    setPoints(unfilteredPoints.current);
  };

  const filterMap = item => {
    var filteredPoints = unfilteredPoints.current.filter(
      each => each.layer === item.name
    );
    setPoints(filteredPoints);
  };

  const fetchMap = props => {
    const profileName = props.match.params.name;
    const key = searchParams.get("key");
    const variantParam = searchParams.get("variant");
    const typeParam = searchParams.get("type");
    const titleParam = searchParams.get("title");
    const layersParam = searchParams.get("layers");

    if (key != null && key !== "") {
      if (
        variantParam &&
        (variantParam === "light" || variantParam === "dark")
      ) {
        setVariant(variantParam);
      }
      if (typeParam === "profile") {
        api
          .getProfileSummaryMapWithToken(profileName, key, "Client")
          .then(map => {
            setMap(map);
            setTitle(titleParam ?? map.owner.displayName);
            setHeaderLogoId(map.owner.logoId);
            setThumbnailContent(
              <ParentContentThumbnail
                imageId={map.owner.coverPhotoId}
                title={map.owner.displayName}
                description={map.owner.description}
              />
            );
            var mapPoints = [...map.stories];
            mapPoints = mapPoints.concat(map.events);
            setPoints(mapPoints);
            setLoading(false);
          })
          .catch(error => {
            setLoading(false);
            setDenied(true);
          });
      } else if (typeParam === "community") {
        api
          .getProfileCommunityWithToken(profileName, key, "Client")
          .then(community => {
            setCommunity(community);
            api
              .getProfileCommunityMapWithToken(
                profileName,
                key,
                "Client"
              )
              .then(map => {
                setTitle(titleParam ?? map.title);

                setHeaderLogoId(map.owner.logoId);
                setThumbnailContent(
                  <ParentContentThumbnail
                    imageId={map.owner.coverPhotoId}
                    title={titleParam ?? map.title}
                  />
                );

                var mapLayers = map.layers;
                if (layersParam) {
                  var b = new Buffer(layersParam, "base64");
                  var decoded = b.toString();
                  mapLayers = JSON.parse(decoded);
                }
                if (mapLayers && mapLayers.length > 0) {
                  setOverrideLayers(mapLayers);
                }

                // Get the data for the map
                var allAPICalls = [];
                if (
                  mapLayers &&
                  mapLayers.findIndex(layer =>
                    layer.value.startsWith("AMBASSADORS")
                  ) > -1
                ) {
                  var creatorPayload = {
                    queryType: "AMBASSADOR",
                    profileType: "AMBASSADOR"
                  };
                  var creatorAPICall = api.queryProfileCommunityMapWithToken(
                    profileName,
                    creatorPayload,
                    key,
                    "Client"
                  );
                  allAPICalls.push(creatorAPICall);
                }
                if (
                  mapLayers &&
                  mapLayers.findIndex(
                    layer =>
                      layer.value.startsWith("BUSINESSES") ||
                      layer.value.startsWith("BUSINESSES.")
                  ) > -1
                ) {
                  var businessPayload = {
                    queryType: "BUSINESS",
                    profileType: "BUSINESS"
                  };
                  var businessSubtypes = mapLayers.filter(layer =>
                    layer.value.startsWith("BUSINESSES.")
                  );
                  if (businessSubtypes && businessSubtypes.length > 0) {
                    var groups = businessSubtypes.map(subtype =>
                      subtype.value.replace("BUSINESSES.", "")
                    );
                    businessPayload["types"] = groups;
                  }
                  var businessAPICall = api.queryProfileCommunityMapWithToken(
                    profileName,
                    businessPayload,
                    key,
                    "Client"
                  );
                  allAPICalls.push(businessAPICall);
                }
                if (
                  mapLayers &&
                  mapLayers.findIndex(layer => layer.value.startsWith("POI")) >
                    -1
                ) {
                  var poiPayload = {
                    queryType: "POI",
                    profileType: "POI"
                  };
                  var poiAPICall = api.queryProfileCommunityMapWithToken(
                    profileName,
                    poiPayload,
                    key,
                    "Client"
                  );
                  allAPICalls.push(poiAPICall);
                }
                if (
                  mapLayers &&
                  mapLayers.findIndex(layer =>
                    layer.value.startsWith("STORIES")
                  ) > -1
                ) {
                  var storyPayload = {
                    queryType: "STORY"
                  };
                  var storyAPICall = api.queryProfileCommunityMapWithToken(
                    profileName,
                    storyPayload,
                    key,
                    "Client"
                  );
                  allAPICalls.push(storyAPICall);
                }
                if (
                  mapLayers &&
                  mapLayers.findIndex(layer => layer.value.startsWith("POST")) >
                    -1
                ) {
                  var postPayload = {
                    queryType: "POST"
                  };
                  var postAPICall = api.queryProfileCommunityMapWithToken(
                    profileName,
                    postPayload,
                    key,
                    "Client"
                  );
                  allAPICalls.push(postAPICall);
                }
                if (
                  mapLayers &&
                  mapLayers.findIndex(layer =>
                    layer.value.startsWith("EVENTS")
                  ) > -1
                ) {
                  var eventPayload = {
                    queryType: "EVENT"
                  };
                  var eventAPICall = api.queryProfileCommunityMapWithToken(
                    profileName,
                    eventPayload,
                    key,
                    "Client"
                  );
                  allAPICalls.push(eventAPICall);
                }
                var allPoints = [];
                Promise.all(allAPICalls)
                  .then(function(queryResults) {
                    queryResults.forEach(function(queryResult, index) {
                      allPoints = allPoints.concat(queryResult.results);
                    });
                    // console.log("EmbeddedMapWidget community map points", allPoints);
                    setPoints(allPoints);
                    unfilteredPoints.current = allPoints;
                    setMap(map);
                    setLoading(false);
                  })
                  .catch(error => {
                    setLoading(false);
                    setDenied(true);
                  });
              })
              .catch(error => {
                setLoading(false);
                setDenied(true);
              });
          });
      } else {
        setLoading(false);
        setDenied(true);
      }
    }
  };

  useEffect(() => {
    fetchMap(props);
  }, [searchParams]);

  useEffect(() => {
    if (denied) router.replace("/403");
  }, [denied, router]);

  if (loading) {
    return <></>;
  } else if (denied) {
    return null;
  } else {
    return (
      <div className="embedded-map-widget d-flex flex-column px-0 vh-100">
        <EmbeddedNavbar
          onMenuClick={triggerSideMenu}
          title={title}
          logoId={headerLogoId}
          variant={variant ?? "light"}
        />
        <div className="map-view-wrapper flex-grow-1 overflow-hidden px-0 position-relative">
          <EmbeddedMapViewWrapper
            {...props}
            width={"100%"}
            height={`calc(100vh - 68px)`}
            addMyLocation={searchParams.get("addMyLocation") === "true"}
            map={map}
            community={community}
            overrideLayers={overrideLayers}
            points={points}
            searchFilter={map.mapType === "COMMUNITY"}
            enableLayerView={true}
            external={true}
          />
          <UralityBrandedModal show={menuTrigger} onHide={triggerSideMenu} centered>
            {thumbnailContent}
          </UralityBrandedModal>
        </div>
      </div>
    );
  }
};

export default EmbeddedMapWidget;
